// filepath: /Users/kishorekota/git/sample_react_app/ui/src/utils/apiClient.js
import { toUserMessage } from './userMessage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// In-flight cache for GET request dedup (F6 of docs/PERFORMANCE.md).
// When two components mount the same hook in the same render pass and both
// call apiClient('/foo'), we'd fire two identical requests. This map dedupes:
// the second caller awaits the first call's promise.
//
// Only GETs are deduped (other verbs are usually intentional and have side
// effects). Entries are removed when the promise settles, so this never grows
// unbounded.
const inFlightGets = new Map();

function inFlightKey(url, headers) {
  // Auth header value matters (different users would share an `_id` URL); body
  // is null for GET. Hashing isn't needed — URLs are short and the map is
  // tiny in practice (typically <10 entries at any moment).
  return `${url}::${headers?.Authorization || ''}`;
}

export const apiClient = async (endpoint, { method = 'GET', body = null, headers = {}, endpointOverride = null } = {}) => {
  const token = localStorage.getItem('authToken');
  const config = {
    method: method,
    headers: {
      ...headers,
    },
  };

  // Add development testing header to bypass rate limits in development
  if (process.env.NODE_ENV === 'development') {
    config.headers['x-development-testing'] = 'true';
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // --- DEBUG: Check the type of the body received by apiClient ---
  console.debug(`[apiClient] Received body type: ${typeof body}`);
  // --- END DEBUG ---

  if (body) {
    // --- Ensure we only stringify if it's not already a string ---
    if (typeof body === 'object') {
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(body);
        console.debug('[apiClient] Stringified object body.');
    } else if (typeof body === 'string') {
        // If it's already a string, assume it's correctly formatted JSON
        // This might hide the root cause but could be a temporary workaround
        // if the source of double-stringification is hard to find.
        // Ideally, find where it's being stringified the first time.
        config.headers['Content-Type'] = 'application/json';
        config.body = body; // Send the string as-is
        console.warn('[apiClient] Received body as a string. Sending as-is. Check for double stringification in calling code.');
    } else {
        console.error('[apiClient] Received body of unexpected type:', typeof body);
        // Handle error or proceed cautiously
    }
  }

  const url = endpointOverride ? `${API_BASE_URL}${endpointOverride}` : `${API_BASE_URL}${endpoint}`;

  // F6: dedupe concurrent GETs to the same URL+auth. The second caller awaits
  // the first call's in-flight promise rather than firing a second request.
  if (method === 'GET') {
    const key = inFlightKey(url, config.headers);
    const existing = inFlightGets.get(key);
    if (existing) {
      console.debug(`[apiClient] Deduplicating concurrent GET to ${url}`);
      return existing;
    }
  }

  console.debug(`[apiClient] Requesting: ${method} ${url}`, { headers: config.headers, body: config.body ? '(Body Set)' : null });

  const runRequest = async () => {
    try {
      const response = await fetch(url, config);

      console.debug(`[apiClient] Raw Response Status: ${response.status} for ${url}`);

      let responseData = null;
      try {
        responseData = await response.json();
        console.debug(`[apiClient] Parsed Response Data for ${url}:`, responseData);
      } catch (jsonError) {
        // Non-JSON body (e.g. an nginx/Cloudflare 5xx HTML page, or an empty
        // body). Leave responseData null and let the unified handler below turn
        // the status into a friendly message rather than a bare "HTTP error N".
        console.warn(`[apiClient] Failed to parse JSON response for ${url}. Status: ${response.status}`, jsonError);
        responseData = null;
      }

      if (!response.ok) {
        // Sanitize for display: never surface internal/vendor names (e.g. Shippo)
        // or raw technical strings; fall back to a friendly per-status message.
        const rawMessage = responseData?.message || responseData?.error || null;
        const errorMessage = toUserMessage(rawMessage, { status: response.status });
        console.error(`[apiClient] Error Response for ${url}:`, errorMessage, responseData);
        // Preserve the structured error so callers can branch on the code
        // (e.g. 'account_on_hold' -> show a verification notice instead of a generic error).
        const err = new Error(errorMessage);
        err.status = response.status;
        err.code = responseData?.error || null;
        err.data = responseData || null;
        throw err;
      }

      return responseData;
    } catch (error) {
      console.error(`[apiClient] Network or other error for ${url}:`, error);
      throw error;
    }
  };

  if (method === 'GET') {
    const key = inFlightKey(url, config.headers);
    const promise = runRequest().finally(() => inFlightGets.delete(key));
    inFlightGets.set(key, promise);
    return promise;
  }
  return runRequest();
};